import { type KeyboardEvent, type MouseEvent, useCallback, useMemo } from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';

import { getUrlForDomainInContext } from '@atlaskit/atlassian-context';
// eslint-disable-next-line no-restricted-imports
import type { EnvironmentType } from '@atlaskit/atlassian-context/src/common/constants';
import { fg } from '@atlaskit/platform-feature-flags';
import { type LinkItemResponse } from '@atlassian/navigation-bff-data-provider';

import { useUniversalCreateState } from '../../../../../context';
import type { CreationContext } from '../../../../../types/crossAppCreation';

function getUrlEnv(serverSessionSyncUrl: string): EnvironmentType | null {
	const url = new URL(serverSessionSyncUrl);

	if (url.hostname === 'id.stg.internal.atlassian.com') {
		return 'staging';
	}

	if (url.hostname === 'id.atlassian.com') {
		return 'prod';
	}

	return null;
}

function compileIcSessionSyncUrl(serverSessionSyncUrl: string): string {
	const env = getUrlEnv(serverSessionSyncUrl);
	const icEnvUrl = env ? getUrlForDomainInContext('id', env) : undefined;
	if (icEnvUrl) {
		const icFinalUrl = new URL(serverSessionSyncUrl);
		const icEnvHostname = new URL(icEnvUrl).hostname;
		icFinalUrl.hostname = icEnvHostname;

		return icFinalUrl.toString();
	}

	return serverSessionSyncUrl;
}

function useBuildLinkItemUrl(item: LinkItemResponse): string {
	const { product, subProduct } = useUniversalCreateState();
	const buildUrlWithAnalyticsParams = useCrossProductUrlWrapper({
		bridge: 'universalCreate',
		product: product,
		subProduct: subProduct,
	});

	if (!item.href || !item.hostname) {
		return '';
	}

	const base = item.hostname.startsWith('https://') ? item.hostname : `https://${item.hostname}`;
	const targetUrl = new URL(item.href, base);

	// Add query parameters from options
	for (const [key, value] of Object.entries(item.options || {})) {
		if (value !== null) {
			targetUrl.searchParams.append(key, String(value));
		}
	}

	// Inject product param for Trello board
	if (item.ati === 'ati:cloud:trello:board' && fg('navx-uc-onboarding-trello') ) {
		targetUrl.searchParams.append('product', product);
	}

	const finalTargetUrl = buildUrlWithAnalyticsParams(targetUrl.toString());

	// Handle session sync URL logic
	if (!item.sessionSyncUrl) {
		// When session sync URL is empty, return the current URL
		return finalTargetUrl;
	}

	// When session sync URL is set, use it as base and set continue param to current URL
	const finalSessionSyncUrl = compileIcSessionSyncUrl(item.sessionSyncUrl);
	const sessionSyncUrl = new URL(finalSessionSyncUrl);
	sessionSyncUrl.searchParams.set('continue', finalTargetUrl);

	return sessionSyncUrl.toString();
}

export function useLinkItemConfig(item: LinkItemResponse, context: CreationContext) {
	const href = useBuildLinkItemUrl(item);
	// Priority: explicit config > item target > default '_blank'
	const target = context.crossAppLinkOpenTarget || item.target || '_blank';

	const onClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			event.preventDefault();
			window.parent.location.href = href;
		},
		[href],
	);

	return useMemo(() => {
		// Only add rel attributes for _blank targets to prevent security issues
		const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

		// Only add onClick handler for _self targets in iframes with valid href to minimize impact
		const shouldAddOnClick = href && target === '_self' && window !== window.parent;

		return {
			target,
			rel,
			href,
			...(shouldAddOnClick && { onClick }),
		};
	}, [href, target, onClick]);
}
